import React from 'react';
import { FaTrophy, FaUsers, FaYoutube, FaDumbbell, FaHeart, FaFire } from 'react-icons/fa';
import channelLogo from '../assets/channels4_profile.jpg';

const About = ({ YOUTUBE_CHANNEL, INSTAGRAM_PROFILE }) => {
  return (
    <div className="min-h-screen bg-black py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 rounded-2xl p-1 mb-8 sm:mb-12">
          <div className="bg-black rounded-2xl p-6 sm:p-8 lg:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div>
                <img
                  src={channelLogo}
                  alt="Coach Sabari"
                  className="w-full max-w-md mx-auto rounded-2xl shadow-2xl border-4 border-yellow-400"
                />
              </div>
              <div className="text-white">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-yellow-400 uppercase tracking-wide mb-4 sm:mb-6">
                  About Coach Sabari
                </h1>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                  Professional fitness trainer with 5+ years of experience in transforming lives through rugged training methods. Specialized in strength training, fat loss, and muscle building.
                </p>
                <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                  My mission is to help you achieve your fitness goals through personalized training programs, proper nutrition guidance, and unwavering motivation.
                </p>
                <div className="flex flex-wrap gap-4">
                  <a
                    href={YOUTUBE_CHANNEL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-bold uppercase transition-all border-2 border-red-500 flex items-center space-x-2"
                  >
                    <FaYoutube />
                    <span>YouTube Channel</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-12">
          {[
            { icon: FaUsers, number: '2+', label: 'Clients Trained', color: 'yellow' },
            { icon: FaTrophy, number: '3+', label: 'Years Experience', color: 'orange' },
            { icon: FaYoutube, number: '380+', label: 'Subscribers', color: 'red' },
            { icon: FaDumbbell, number: '80+', label: 'Workout Videos', color: 'yellow' }
          ].map((stat, index) => (
            <div
              key={index}
              className={`bg-gray-900 border-2 border-${stat.color}-500/50 rounded-xl p-6 text-center hover:border-${stat.color}-400 transition-all`}
            >
              <stat.icon className={`text-${stat.color}-400 text-4xl mx-auto mb-3`} />
              <div className={`text-3xl font-bold text-${stat.color}-400 mb-2`}>{stat.number}</div>
              <div className="text-gray-400 text-sm uppercase">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Expertise Section */}
        <div className="bg-gray-900 rounded-2xl p-6 sm:p-8 lg:p-12 border-2 border-yellow-500/30 mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl font-bold text-yellow-400 uppercase tracking-wide mb-6 sm:mb-8 text-center">
            My Expertise
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: FaDumbbell,
                title: 'Strength Training',
                description: 'Build muscle and increase strength with proven progressive overload techniques',
                color: 'yellow'
              },
              {
                icon: FaFire,
                title: 'Fat Loss',
                description: 'Effective fat burning strategies combining HIIT, cardio, and nutrition',
                color: 'orange'
              },
              {
                icon: FaHeart,
                title: 'Nutrition Coaching',
                description: 'Personalized meal plans designed for your specific fitness goals',
                color: 'red'
              }
            ].map((expertise, index) => (
              <div
                key={index}
                className={`bg-black border-2 border-${expertise.color}-500/50 rounded-xl p-6 hover:border-${expertise.color}-400 transition-all`}
              >
                <div className={`bg-gradient-to-r from-${expertise.color}-500 to-${expertise.color}-600 w-16 h-16 rounded-lg flex items-center justify-center mb-4`}>
                  <expertise.icon className="text-black text-2xl" />
                </div>
                <h3 className={`text-xl font-bold text-${expertise.color}-400 uppercase mb-3`}>
                  {expertise.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {expertise.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Philosophy Section */}
        <div className="bg-gradient-to-r from-yellow-500 via-orange-500 to-red-600 rounded-2xl p-1">
          <div className="bg-black rounded-2xl p-6 sm:p-8 lg:p-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-yellow-400 uppercase tracking-wide mb-4 sm:mb-6 text-center">
              Training Philosophy
            </h2>
            <div className="max-w-3xl mx-auto">
              <blockquote className="text-xl text-gray-300 italic text-center mb-8 leading-relaxed">
                "Rugged training builds rugged character. Your mind is your strongest muscle. 
                Success in fitness comes from consistency, dedication, and the willingness to push beyond your limits."
              </blockquote>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gray-900 border-2 border-yellow-500/50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-yellow-400 uppercase mb-3">Core Values</h3>
                  <ul className="space-y-2 text-gray-400 text-sm">
                    <li>• Discipline & Consistency</li>
                    <li>• Progressive Overload</li>
                    <li>• Proper Form & Technique</li>
                    <li>• Nutrition First</li>
                    <li>• Mental Toughness</li>
                  </ul>
                </div>
                <div className="bg-gray-900 border-2 border-orange-500/50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-orange-400 uppercase mb-3">Training Approach</h3>
                  <ul className="space-y-2 text-gray-400 text-sm">
                    <li>• Personalized Programs</li>
                    <li>• Science-Based Methods</li>
                    <li>• Goal-Oriented Training</li>
                    <li>• Continuous Support</li>
                    <li>• Results-Driven</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
